import React, { memo, useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader } from "reactstrap";
import "./DealInfoCards.css";
import "./DealInfoCardsTheme2.css";
import {
  formatAccountStatusLabel,
  getAccountStatusColor,
} from "utils/accountStatusColors";

const NEUTRAL_ACCENT_BORDER = "rgb(194, 218, 231)";
const TRADE_IN_ACCENT_BORDER = "#cce5ff";
const SURFACE_LIGHT = "#ffffff";
const SURFACE_DARK = "#272A3D";

function getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor) {
    return {
        width: "100%",
        background: isLightTheme ? SURFACE_LIGHT : SURFACE_DARK,
        "--dic2-accent-border": accentBorderColor,
        boxShadow: "none",
    };
}

function getLegacyCardStyle(accentBorderColor) {
    return {
        width: "100%",
        borderLeft: `8px solid ${accentBorderColor}`,
    };
}

function getDealStatusAccentColor(dealStatus) {
    return dealStatus === "working"
        ? "#0092e0"
        : dealStatus === "prospect"
            ? "#16A34A"
            : dealStatus === "contracted"
                ? "#b98f06"
                : dealStatus === "spot_delivered"
                    ? "#9d6413"
                    : dealStatus === "cancelled"
                        ? "#db0000"
                        : dealStatus === "contracted_booked"
                            ? "#624E88"
                            : "#dc3545";
}

const DealInfoCards = memo(({ data, activeColor, getActiveColorStyle, isLightTheme, vehicleAge, daysInInventory, formatUSD, isTheme2 = false, alwaysExpanded = false }) => {

    const { t } = useTranslation();
    const [showVehicleDetailsMap, setShowVehicleDetailsMap] = useState({});
    const [showApplicantDetails, setShowApplicantDetails] = useState(() => window.innerWidth > 991);
    const [showDealDetails, setShowDealDetails] = useState(() => window.innerWidth > 991);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);

    const isTheme2MobileExpanded = isTheme2 && isMobile;
    const showMoreLessLinks = !isTheme2;

    const wrapTheme2ExtraRows = (content) => {
        if (isTheme2MobileExpanded || alwaysExpanded) return content;
        return <div className="dic2-extra-rows">{content}</div>;
    };

    const [currentTheme, setCurrentTheme] = useState(() => {
        const darkMode = localStorage.getItem("darkMode");
        return darkMode ? JSON.parse(darkMode) : true;
    });

    const getTextColor = () => {
        if (isTheme2) {
            return isLightTheme ? "#3c4858" : "#ffffff";
        }
        return currentTheme ? "white" : "black";
    };

    const getCardStyle = (accentBorderColor) =>
        isTheme2
            ? getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor)
            : getLegacyCardStyle(accentBorderColor);

    const cardClassName = isTheme2
        ? classNames("dic2-card", { "dic2-card--always-expanded": alwaysExpanded })
        : undefined;
    const cardBodyClassName = isTheme2 ? "dic2-card-body" : undefined;

    // Format text by removing underscores and capitalizing each word
    const formatDisplayText = (text) => {
        if (!text) return '-';
        return text
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const capitalizeWords = (str) => {
        if (!str) return "-";
        return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // Get all vehicles from inventory_vehicles array
    const vehiclesList = useMemo(() => {
        if (data?.inventory_vehicles && Array.isArray(data.inventory_vehicles) && data.inventory_vehicles.length > 0) {
            return data.inventory_vehicles
                .filter(iv => iv?.inventory?.inventory_vehicle)
                .map((iv, index) => ({
                    vehicleData: iv.inventory.inventory_vehicle,
                    stockNumber: iv.inventory.stock_number,
                    documentId: iv.inventory.documentId,
                    index: index
                }));
        }
        // Fallback to old structure if inventory_vehicles doesn't exist
        if (data?.inventory_vehicle) {
            return [{
                vehicleData: data.inventory_vehicle,
                stockNumber: data.inventory_vehicle?.stock_number,
                index: 0
            }];
        }
        return [];
    }, [data]);

    // Match AccountsInfoCards: account card border + acct status label/colors
    const accountLinkedStatusDisplay = useMemo(() => {
        const actStatus = data?.act_status ?? data?.calculus?.act_status;
        const isBankruptcy = Boolean(
          data?.is_bankruptcy ?? data?.calculus?.is_bankruptcy
        );
        const statusColor = getAccountStatusColor(actStatus, {
          flags: data?.flags ?? data?.calculus?.flags,
          isBankruptcy,
        });
        const displayText = formatAccountStatusLabel(actStatus, {
            isLock: !!(data?.is_lock ?? data?.calculus?.is_lock),
            flags: data?.flags ?? data?.calculus?.flags,
            isBankruptcy,
        });
        return {
            borderLeftColor: statusColor,
            statusColor,
            displayText,
        };
    }, [
        data?.act_status,
        data?.calculus?.act_status,
        data?.is_lock,
        data?.calculus?.is_lock,
        data?.is_bankruptcy,
        data?.calculus?.is_bankruptcy,
        data?.flags,
        data?.calculus?.flags,
    ]);

    const toggleVehicleDetails = (index) => {
        setShowVehicleDetailsMap(prev => {
            const isCurrentlyOpen = prev[index] !== undefined ? prev[index] : window.innerWidth > 991;
            return {
                ...prev,
                [index]: !isCurrentlyOpen
            };
        });
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            const darkMode = localStorage.getItem("darkMode");
            const newTheme = darkMode ? JSON.parse(darkMode) : true;
            setCurrentTheme(newTheme);
        };

        window.addEventListener("storage", handleStorageChange);

        const interval = setInterval(() => {
            const darkMode = localStorage.getItem("darkMode");
            const newTheme = darkMode ? JSON.parse(darkMode) : true;
            if (newTheme !== currentTheme) {
                setCurrentTheme(newTheme);
            }
        }, 100);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Separate vehicles into rows with max 4 cards per row
    // First row: Applicant Detail (1) + Deal Detail (1) + Vehicle Detail 1 (1) + Vehicle Detail 2 (1) = 4 cards
    // Second row: Vehicle Detail 3-6 (4 cards)
    // Third row: Vehicle Detail 7+ (remaining vehicles)
    const firstRowVehicles = vehiclesList.slice(0, 2); // First 2 vehicles (for first row with 2 fixed cards)
    const secondRowVehicles = vehiclesList.slice(2, 6); // Vehicles 3-6 (4 vehicles for second row)
    const thirdRowVehicles = vehiclesList.slice(6); // Vehicle 7 and onwards (for third row)

    const renderVehicleCard = (vehicle, idx, originalIndex) => {
        const isDetailsOpen = isTheme2MobileExpanded
            ? true
            : showVehicleDetailsMap[originalIndex] !== undefined
                ? showVehicleDetailsMap[originalIndex]
                : window.innerWidth > 991;

        const showVehicleExtra = !isTheme2 ? isDetailsOpen : true;

        const vehicleExtraRows = (
            <>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.bodyStyle')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.body_style || '-'}</span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.vin')}:</span>
                    <span className="aic-value">
                        {vehicle.vehicleData?.vin ? (
                            <a
                                href={`/inventory/${vehicle?.documentId}`}
                                target="_blank"
                                style={{ color: getTextColor(), textDecoration: 'underline', fontWeight: '600' }}
                            >
                                {vehicle.vehicleData?.vin}
                            </a>
                        ) : '-'}
                    </span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.trim')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.trim || '-'}</span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.color')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.exterior_color || '-'}</span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.mileage')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.actual_mileage || '-'}</span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.condition')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.condition || '-'}</span>
                </div>
                <div className="aic-row">
                    <span className="aic-label">{t('dealInfoCards.type')}:</span>
                    <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.type?.toLocaleUpperCase() || '-'}</span>
                </div>
            </>
        );

        return (
            <Card
                key={originalIndex}
                className={cardClassName}
                style={getCardStyle(NEUTRAL_ACCENT_BORDER)}
            >
                <CardHeader className="pt-1">
                    <h4 className="mb-0">
                        {t('dealInfoCards.vehicleDetail')}
                        {vehiclesList.length > 1 && ` ${originalIndex + 1}`}
                    </h4>
                </CardHeader>
                <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
                    <div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.stockNumber')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.stockNumber || '-'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.description')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>
                                {vehicle.vehicleData?.make} {vehicle.vehicleData?.model}
                            </span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.year')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{vehicle.vehicleData?.year || '-'}</span>
                        </div>
                        {showVehicleExtra && (
                            isTheme2
                                ? wrapTheme2ExtraRows(vehicleExtraRows)
                                : vehicleExtraRows
                        )}
                        {showMoreLessLinks && (
                        <div className="mb-1">
                            <a
                                href="#vehicle-details"
                                className="aic-link"
                                style={{ color: getActiveColorStyle().borderColor }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggleVehicleDetails(originalIndex);
                                }}
                            >
                                {isDetailsOpen ? t('dealInfoCards.lessDetails') : t('dealInfoCards.moreDetails')}
                            </a>
                        </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        );
    };

    const renderTradeInCard = (tradeInData, label) => {
        if (!tradeInData) return null;

        return (
            <Card
                key={label}
                className={classNames(cardClassName, "dic2-trade-in-card")}
            style={getCardStyle(TRADE_IN_ACCENT_BORDER)}
            >
                <CardHeader className="pt-1">
                    <h4 className="mb-0">{label}</h4>
                </CardHeader>
                <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
                    <div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.description')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>
                                {tradeInData.year} {tradeInData.make} {tradeInData.model}
                            </span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.stockNumber')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{tradeInData.stock_number || '-'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">Trade-In VIN:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{tradeInData.vin || '-'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.mileage')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{tradeInData.mileage || '-'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">{t('dealInfoCards.condition')}:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{tradeInData.condition || '-'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">Title Status:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{tradeInData.title_status || 'Unknown Status'}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">Offer Price:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{formatUSD ? formatUSD(tradeInData.offer_price) : `$${tradeInData.offer_price || '0.00'}`}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">Valuation:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{formatUSD ? formatUSD(tradeInData.valuation) : `$${tradeInData.valuation || '0.00'}`}</span>
                        </div>
                        <div className="aic-row">
                            <span className="aic-label">Lien Balance:</span>
                            <span className="aic-value" style={{ color: getTextColor() }}>{formatUSD ? formatUSD(tradeInData.lien_balance) : `$${tradeInData.lien_balance || '0.00'}`}</span>
                        </div>
                    </div>
                </CardBody>
            </Card>
        );
    };

    return (
        <div
            className={classNames("d-flex flex-column", {
                "deal-info-cards-theme2": isTheme2,
                "deal-info-cards-theme2--light": isTheme2 && isLightTheme,
                "deal-info-cards-theme2--dark": isTheme2 && !isLightTheme,
            })}
            style={{ gap: 12 }}
        >
            {/* First Row: Applicant Detail, Deal Detail, Vehicle Detail 1, Vehicle Detail 2 (max 4 cards) */}
            <div className="d-flex flex-column flex-lg-row" style={{ gap: 12 }}>
                <Card className={cardClassName} style={getCardStyle(NEUTRAL_ACCENT_BORDER)}>
                    <CardHeader className="pt-1">
                        <h4 className="mb-0">
                            {data?.deal_type === 'wholesale_deal' ? t('dealInfoCards.wholesalerDetail') : t('dealInfoCards.applicantDetail')}
                        </h4>
                    </CardHeader>
                    <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
                        {data?.deal_type === 'wholesale_deal' ? (
                            <div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('dealInfoCards.wholesaler')}:</span>
                                    <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.name || data?.wholesaler?.legal_name || '-'}</span>
                                </div>
                                {data?.wholesaler?.phone_1 && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.phone')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.phone_1 || '-'}</span>
                                    </div>
                                )}
                                {data?.wholesaler?.address_1 && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.address1')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.address_1 || '-'}</span>
                                    </div>
                                )}
                                {data?.wholesaler?.address_2 && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.address2')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.address_2 || '-'}</span>
                                    </div>
                                )}
                                {((!isTheme2 && showApplicantDetails) || isTheme2) && (
                                    isTheme2 ? wrapTheme2ExtraRows(
                                    <>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.city')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.city || '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.state')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>
                                                {data?.wholesaler?.state?.abbreviation || data?.wholesaler?.state?.name || (typeof data?.wholesaler?.state === 'string' ? data?.wholesaler?.state : '') || data?.wholesaler?.state_name || '-'}
                                            </span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.zipCode')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.zip || '-'}</span>
                                        </div>
                                    </>
                                    ) : (
                                    <>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.city')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.city || '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.state')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>
                                                {data?.wholesaler?.state?.abbreviation || data?.wholesaler?.state?.name || (typeof data?.wholesaler?.state === 'string' ? data?.wholesaler?.state : '') || data?.wholesaler?.state_name || '-'}
                                            </span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.zipCode')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.wholesaler?.zip || '-'}</span>
                                        </div>
                                    </>
                                    )
                                )}
                            </div>
                        ) : (
                            <div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('dealInfoCards.applicant')}:</span>
                                    <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.full_name ? capitalizeWords(data?.customer?.full_name) : '-'}</span>
                                </div>
                                {data?.customer?.cell_phone && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.phone')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.cell_phone || '-'}</span>
                                    </div>
                                )}
                                {data?.customer?.current_address?.address_1 && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.address1')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.address_1 || '-'}</span>
                                    </div>
                                )}
                                {data?.customer?.current_address?.address_2 && (
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.address2')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.address_2 || '-'}</span>
                                    </div>
                                )}
                                {((!isTheme2 && showApplicantDetails) || isTheme2) && (
                                    isTheme2 ? wrapTheme2ExtraRows(
                                    <>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.city')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.city ? capitalizeWords(data?.customer?.current_address?.city) : '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.state')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.state_name || '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.zipCode')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.zip_code || '-'}</span>
                                        </div>
                                    </>
                                    ) : (
                                    <>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.city')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.city ? capitalizeWords(data?.customer?.current_address?.city) : '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.state')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.state_name || '-'}</span>
                                        </div>
                                        <div className="aic-row">
                                            <span className="aic-label">{t('dealInfoCards.zipCode')}:</span>
                                            <span className="aic-value" style={{ color: getTextColor() }}>{data?.customer?.current_address?.zip_code || '-'}</span>
                                        </div>
                                    </>
                                    )
                                )}
                            </div>
                        )}
                        {showMoreLessLinks &&
                            ((data?.deal_type === 'wholesale_deal' && data?.wholesaler) ||
                            (data?.deal_type !== 'wholesale_deal' && data?.customer?.cell_phone && data?.customer?.current_address?.address_1)) && (
                            <div className="mb-1">
                                <a
                                    href="#details"
                                    className="aic-link"
                                    style={{ color: getActiveColorStyle().borderColor }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowApplicantDetails(!showApplicantDetails);
                                    }}
                                >
                                    {showApplicantDetails ? t('dealInfoCards.lessDetails') : t('dealInfoCards.moreDetails')}
                                </a>
                            </div>
                        )}
                    </CardBody>
                </Card>
                <Card
                    className={cardClassName}
                    style={getCardStyle(getDealStatusAccentColor(data?.deal_status))}
                >
                    <CardHeader className="pt-1">
                        <h4 className="mb-0">{t('dealInfoCards.dealDetail')}</h4>
                    </CardHeader>
                    <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
                        <div>
                            <div className="aic-row">
                                <span className="aic-label">{t('dealInfoCards.dealId')}:</span>
                                <span className="aic-value" style={{ color: getTextColor() }}>{data?.deal_id || '-'}</span>
                            </div>
                            <div className="aic-row">
                                <span className="aic-label">{t('dealInfoCards.dealType')}:</span>
                                <span className="aic-value" style={{ color: getTextColor() }}>{formatDisplayText(data?.deal_type)}</span>
                            </div>
                            <div className="aic-row">
                                <span className="aic-label">{t('dealInfoCards.status')}:</span>
                                <span className="aic-value" style={{ color: data?.deal_status === "working" ? "#0092e0" : data?.deal_status === "prospect" ? "#16A34A" : data?.deal_status === "contracted" ? "#b98f06" : data?.deal_status === "spot_delivered" ? "#9d6413" : data?.deal_status === "cancelled" ? "#db0000" : data?.deal_status === "contracted_booked" ? "#624E88" : "#dc3545" }}>{formatDisplayText(data?.deal_status)}</span>
                            </div>
                            {((!isTheme2 && showDealDetails) || isTheme2) && (
                                isTheme2 ? wrapTheme2ExtraRows(
                                <>
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.source')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{formatDisplayText(data?.deal_type) || '-'}</span>
                                    </div>
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.applicationType')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{formatDisplayText(data?.application_type) || '-'}</span>
                                    </div>
                                </>
                                ) : (
                                <>
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.source')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{formatDisplayText(data?.deal_type) || '-'}</span>
                                    </div>
                                    <div className="aic-row">
                                        <span className="aic-label">{t('dealInfoCards.applicationType')}:</span>
                                        <span className="aic-value" style={{ color: getTextColor() }}>{formatDisplayText(data?.application_type) || '-'}</span>
                                    </div>
                                </>
                                )
                            )}
                            {data?.desired_vehicle && (
                                <div className="aic-row ">
                                    <span className="aic-label">Desired Vehicle:</span>
                                    <span className="aic-value" style={{ color: getTextColor(), fontWeight: '600' }}>
                                        {data.desired_vehicle.is_empty || !data.desired_vehicle.inventory
                                            ? "Any Year Any Make Any Model"
                                            : `${data.desired_vehicle.inventory?.inventory_vehicle?.year || ''} ${data.desired_vehicle.inventory?.inventory_vehicle?.make || ''} ${data.desired_vehicle.inventory?.inventory_vehicle?.model || ''}`.trim() || '-'
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                        {showMoreLessLinks && (
                        <div className="mb-1">
                            <a
                                href="#deal-details"
                                className="aic-link"
                                style={{ color: getActiveColorStyle().borderColor }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowDealDetails(!showDealDetails);
                                }}
                            >
                                {showDealDetails ? t('dealInfoCards.lessDetails') : t('dealInfoCards.moreDetails')}
                            </a>
                        </div>
                        )}
                    </CardBody>
                </Card>
                {data?.calculus &&
                    <Card
                        className={cardClassName}
                        style={getCardStyle(accountLinkedStatusDisplay.borderLeftColor)}
                    >
                        <CardHeader className="pt-1">
                            <h4 className="mb-0">{t('dealInfoCards.accountDetail')}</h4>
                        </CardHeader>
                        <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
                            <div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('dealInfoCards.acct_number')}:</span>
                                    <span className="aic-value" ><a href={`/accounts/${data?.calculus?.booking_id}`} target="_blank" style={{ color: getTextColor(), textDecoration: 'underline', fontWeight: '600' }}>{data?.calculus?.booking_id || '-'}</a></span>
                                </div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('dealInfoCards.salesLocation')}:</span>
                                    <span className="aic-value" style={{ color: getTextColor() }}> <a href={`/channels/${data?.inventory_vehicles[0]?.inventory?.location?.channel_id}`} target="_blank" style={{ color: getTextColor(), textDecoration: 'underline', fontWeight: '600' }}>{data?.inventory_vehicles[0]?.inventory?.location?.channel_id + " - " + data?.inventory_vehicles[0]?.inventory?.location?.legal_name || '-'}</a></span>
                                </div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('dealInfoCards.bookeepingCo')}:</span>
                                    <span className="aic-value" style={{ color: getTextColor() }}> <a href={`/channels/${data?.inventory_vehicles[0]?.inventory?.location?.sales_location?.bookkeeping_company?.channel_id}`} target="_blank" style={{ color: getTextColor(), textDecoration: 'underline', fontWeight: '600' }}>{data?.inventory_vehicles[0]?.inventory?.location?.sales_location?.bookkeeping_company?.channel_id + " - " + data?.inventory_vehicles[0]?.inventory?.location?.sales_location?.bookkeeping_company?.legal_name || '-'}</a></span>
                                </div>
                                <div className="aic-row">
                                    <span className="aic-label">{t('accountsInfoCards.acctStatus')}:</span>
                                    <span
                                        className="aic-value"
                                        style={{ color: accountLinkedStatusDisplay.statusColor }}
                                    >
                                        {accountLinkedStatusDisplay.displayText}
                                    </span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                }
                {firstRowVehicles.map((vehicle, idx) => renderVehicleCard(vehicle, idx, vehicle.index))}
            </div>

            {/* Second Row: Vehicle Detail 3-6 (max 4 cards) */}
            {
                secondRowVehicles.length > 0 && (
                    <div className="d-flex flex-column flex-lg-row" style={{ gap: 12 }}>
                        {secondRowVehicles.map((vehicle, idx) => renderVehicleCard(vehicle, idx, vehicle.index))}
                    </div>
                )
            }

            {/* Third Row: Vehicle Detail 7 and onwards */}
            {
                thirdRowVehicles.length > 0 && (
                    <div className="d-flex flex-column flex-lg-row" style={{ gap: 12 }}>
                        {thirdRowVehicles.map((vehicle, idx) => renderVehicleCard(vehicle, idx, vehicle.index))}
                    </div>
                )
            }

            {/* Trade-In Row */}
            {(data?.trade_in_1 || data?.trade_in_2) && (
                <div className="d-flex flex-column flex-lg-row dic2-trade-in-row" style={{ gap: 12 }}>
                    {renderTradeInCard(data.trade_in_1, "TRADE-IN 1")}
                    {renderTradeInCard(data.trade_in_2, "TRADE-IN 2")}
                </div>
            )}
        </div>
    );

}, (prevProps, nextProps) => {
    // Custom comparison function - only re-render if data actually changes
    return (
        prevProps.data === nextProps.data &&
        prevProps.activeColor === nextProps.activeColor &&
        prevProps.vehicleAge === nextProps.vehicleAge &&
        prevProps.daysInInventory === nextProps.daysInInventory &&
        prevProps.isTheme2 === nextProps.isTheme2 &&
        prevProps.isLightTheme === nextProps.isLightTheme
    );
});

DealInfoCards.displayName = 'DealInfoCards';

export default DealInfoCards; 